import { Button, Col, DatePicker, Form, Input, Row, Select, Space } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { Client } from '../../types/Client';
import ToastNotification from '../../utils/ToastNotification';

const { RangePicker } = DatePicker;

interface JobListSearchFiltersInterfaceProps {
    wrapperStyleClass?: string;
    isLoadingData: boolean;
    clients: Client[];
    initialFilters: any;
    onSetSearchFilters(filters: any, opts?: { isPaging: boolean }): void;
}

const RequestLogListSearchFilters: React.FC<JobListSearchFiltersInterfaceProps> = (
    props: JobListSearchFiltersInterfaceProps,
): React.ReactElement => {
    const { wrapperStyleClass, isLoadingData, clients, onSetSearchFilters: setFiltersToApply, initialFilters } = props;
    const [componentsWithErrors, setComponentsWithErrors] = useState<string[]>([]);

    const [form] = Form.useForm();

    const METHOD_VERBS = ['GET', 'POST', 'PATCH', 'PUT', 'DELETE'];
    const PAYLOADS_SEARCH_OPTIONS = [
        { name: 'rawRequestHeaders', label: 'Request Headers' },
        { name: 'requestPayload', label: 'Request Payload' },
        { name: 'rawResponseHeaders', label: 'Response Headers' },
        { name: 'responsePayload', label: 'Response Payload' },
    ];

    const clientList = [
        { value: 'null', key: 0, text: '- Not defined -' },
        ...clients
            .map((client) => ({
                value: client.id,
                key: client.id,
                text: `${client.name} (${client.stuartClientId || 'n/a'})`,
            }))
            .sort((a, b) => a.text.localeCompare(b.text)),
    ];

    const hasValidFilter = (filters: { [key: string]: unknown }): boolean => {
        return Object.values(filters).some((filter) => typeof filter !== 'undefined');
    };

    const removeDirtyFilters = (filters: { [key: string]: any }): { [key: string]: any } => {
        const filtersToApply = Object.assign(filters);
        Object.keys(filtersToApply).forEach(
            (key) =>
                (!filtersToApply[key] || (Array.isArray(filtersToApply[key]) && filtersToApply[key].length === 0)) &&
                delete filtersToApply[key],
        );
        return filtersToApply;
    };

    const setSearchFilters = (): void => {
        const filters = removeDirtyFilters(form.getFieldsValue());

        if (!filters['payload-search-type'] && filters['payload-search-text']) {
            ToastNotification({
                type: 'error',
                message: 'Missing fields on Payloads search',
                description: 'Payload type must be selected for payload search',
            });
            setComponentsWithErrors([...componentsWithErrors, 'payload-search-type']);
            return;
        }
        if (filters['payload-search-type'] && !filters['payload-search-text']) {
            ToastNotification({
                type: 'error',
                message: 'Missing fields on Payloads search',
                description: 'Payload text must be filled for payload search',
            });
            setComponentsWithErrors([...componentsWithErrors, 'payload-search-text']);
            return;
        }
        setComponentsWithErrors([]);

        if (filters['date-range'] && filters['date-range'].length > 0) {
            filters.from = filters['date-range'][0].format('YYYY-MM-DD HH:mm:ss');
            filters.to = filters['date-range'][1].format('YYYY-MM-DD HH:mm:ss');
            delete filters['date-range'];
        }
        if (filters['payload-search-type'] && filters['payload-search-text']) {
            filters[filters['payload-search-type']] = filters['payload-search-text'];
            delete filters['payload-search-type'];
            delete filters['payload-search-text'];
        }
        if (filters.clientId === 'null') filters.clientId = '';

        if (filters.responseCodeOption === '>=') {
            filters.responseCodeFrom = filters.responseCode;
            delete filters.responseCode;
        }
        if (filters.responseCodeOption === '<=') {
            filters.responseCodeTo = filters.responseCode;
            delete filters.responseCode;
        }
        delete filters.responseCodeOption;

        if (hasValidFilter(filters)) {
            setFiltersToApply({ ...filters });
            return;
        }
        setFiltersToApply({});
        ToastNotification({
            message: 'No search criteria',
            description: 'Job list will be reloaded.',
        });
    };

    const resetFilters = (): void => {
        form.resetFields();
        setFiltersToApply({});
    };

    useEffect(() => {
        const newInitialFilters = { ...initialFilters };
        if (newInitialFilters.from && newInitialFilters.to) {
            newInitialFilters['date-range'] = [moment(newInitialFilters.from), moment(newInitialFilters.to)];
        }
        if (newInitialFilters.requestPayload) {
            newInitialFilters['payload-search-type'] = 'requestPayload';
            newInitialFilters['payload-search-text'] = newInitialFilters.requestPayload;
        }
        if (newInitialFilters.responsePayload) {
            newInitialFilters['payload-search-type'] = 'responsePayload';
            newInitialFilters['payload-search-text'] = newInitialFilters.responsePayload;
        }
        if (newInitialFilters.rawResponseHeaders) {
            newInitialFilters['payload-search-type'] = 'rawResponseHeaders';
            newInitialFilters['payload-search-text'] = newInitialFilters.rawResponseHeaders;
        }
        if (newInitialFilters.rawRequestHeaders) {
            newInitialFilters['payload-search-type'] = 'rawRequestHeaders';
            newInitialFilters['payload-search-text'] = newInitialFilters.rawRequestHeaders;
        }

        if (newInitialFilters.clientId === '') newInitialFilters.clientId = 'null';

        if (newInitialFilters.responseCodeFrom) {
            newInitialFilters.responseCodeOption = '>=';
            newInitialFilters.responseCode = newInitialFilters.responseCodeFrom;
            delete newInitialFilters.responseCodeFrom;
        }
        if (newInitialFilters.responseCodeTo) {
            newInitialFilters.responseCodeOption = '<=';
            newInitialFilters.responseCode = newInitialFilters.responseCodeTo;
            delete newInitialFilters.responseCodeTo;
        }

        form.setFieldsValue(newInitialFilters);
    }, [initialFilters]);

    return (
        <div className={wrapperStyleClass || 'px-4 bg-white'}>
            <Form layout="vertical" form={form} id="filter-form" name="filter-form">
                <Row>
                    <Col flex="auto">
                        <Row gutter={12}>
                            <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                                <Form.Item label="Request Id" id="requestId" name="requestId">
                                    <Input
                                        placeholder="Request Id"
                                        id="requestId"
                                        name="requestId"
                                        autoComplete="off"
                                        onPressEnter={setSearchFilters}
                                        allowClear
                                        autoFocus
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                                <Form.Item label="URL" id="url" name="url">
                                    <Input
                                        placeholder="URL"
                                        id="url"
                                        name="url"
                                        autoComplete="off"
                                        onPressEnter={setSearchFilters}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 4 }}>
                                <Form.Item label="Method" id="method" name="method">
                                    <Select
                                        id="method"
                                        showSearch
                                        placeholder="Select method"
                                        style={{ width: '100%' }}
                                        optionFilterProp="children"
                                        filterOption={(input: string, option): boolean =>
                                            option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        showArrow
                                        allowClear
                                    >
                                        {METHOD_VERBS.map((item: string) => (
                                            <Select.Option key={item} value={`${item}`}>
                                                {item}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 4 }}>
                                <Form.Item label="Response Code" id="responseCode" name="responseCode">
                                    <Input
                                        addonBefore={
                                            <Form.Item name="responseCodeOption" noStyle initialValue="=">
                                                <Select
                                                    options={[
                                                        { value: '=', label: '=' },
                                                        { value: '>=', label: '≥' },
                                                        { value: '<=', label: '≤' },
                                                    ]}
                                                />
                                            </Form.Item>
                                        }
                                        placeholder="Response Code"
                                        id="responseCodeValue"
                                        name="responseCodeValue"
                                        autoComplete="off"
                                        onPressEnter={setSearchFilters}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={12}>
                            <Col span={10} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                                <Form.Item label="Payloads search" id="payload-search-type" name="payload-search-type">
                                    <Select
                                        id="payload-type"
                                        placeholder="Select payload type"
                                        style={{
                                            width: '100%',
                                            border: componentsWithErrors.includes('payload-search-type')
                                                ? '1px solid #F59794'
                                                : '',
                                        }}
                                        optionFilterProp="children"
                                        showArrow
                                        allowClear
                                        showSearch
                                    >
                                        {PAYLOADS_SEARCH_OPTIONS.map((item) => (
                                            <Select.Option key={item.name} value={`${item.name}`}>
                                                {item.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item id="payload-search-text" name="payload-search-text">
                                    <Input
                                        placeholder="Payload text"
                                        id="payload-text"
                                        name="payload-text"
                                        autoComplete="off"
                                        onPressEnter={setSearchFilters}
                                        allowClear
                                        style={{
                                            border: componentsWithErrors.includes('payload-search-text')
                                                ? '1px solid #F59794'
                                                : '',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={10} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} title="Clients">
                                <Form.Item label="Clients" id="clientId" name="clientId">
                                    <Select
                                        id="clientId"
                                        showSearch
                                        placeholder="Select clients: Client name (Stuart client ID)"
                                        style={{ width: '100%' }}
                                        optionFilterProp="children"
                                        filterOption={(input, option): boolean =>
                                            option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        showArrow
                                        allowClear
                                    >
                                        {clientList.map((item: { value: any; key: number; text: string }) => (
                                            <Select.Option key={item.key} value={`${item.value}`}>
                                                {item.text}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                                <Form.Item label="Time range" id="date-range" name="date-range">
                                    <RangePicker
                                        showTime={{
                                            hideDisabledOptions: true,
                                            defaultValue: [
                                                moment('00:00:00', 'HH:mm:ss'),
                                                moment('11:59:59', 'HH:mm:ss'),
                                            ],
                                        }}
                                        format="YYYY-MM-DD HH:mm:ss"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>

                    <Col flex="none" style={{ paddingLeft: 20 }}>
                        <Row gutter={24}>
                            <Col
                                span={24}
                                xs={{ span: 24 }}
                                sm={{ span: 24 }}
                                md={{ span: 24 }}
                                className="d-flex flex-row align-items-center justify-content-end"
                            >
                                <Space>
                                    <Button
                                        id="btn-search"
                                        name="btn-search"
                                        title="Search"
                                        onClick={setSearchFilters}
                                        disabled={isLoadingData}
                                        className="ant-btn text-white btn-primary bg-primary"
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        id="btn-reset-filters"
                                        name="btn-reset-filters"
                                        title="Reset"
                                        onClick={resetFilters}
                                        disabled={isLoadingData}
                                        className="ant-btn"
                                    >
                                        Reset
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default RequestLogListSearchFilters;
